"use client";

import { createApi } from "@reduxjs/toolkit/query/react";
import identity from "lodash/identity";
import pickBy from "lodash/pickBy";
import qs from "qs";

import baseQuery from "@/libs/shared/api/baseQuery";
import { TrainingPlanModel } from "@/libs/customers/pages/TrainingPlans/TrainingPlan/TrainingPlan.types";

const tagTypes = [
  "TrainingPlans" as const,
  "ExerciseLibraryList" as const,
  "Exercise" as const,
];

export const invalidateTrainingPlans = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(trainingPlansApi.util.invalidateTags([tag as any]));
      });
    });
  };
};

export const trainingPlansApi = createApi({
  reducerPath: "trainingPlansApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getTrainingPlans: builder.query({
      query: ({
        page,
        perPage,
        search,
        template,
        orderBy,
        orderByDirection,
      }: {
        perPage: number;
        page: string;
        search?: string;
        template: string;
        orderBy?: string;
        orderByDirection?: string;
      }) => {
        const query = qs.stringify(
          pickBy(
            {
              page,
              per_page: perPage,
              name: search,
              template,
              order_by: orderBy,
              order_by_direction: orderByDirection,
            },
            identity,
          ),
        );

        return {
          url: `pt/training_cards?${query}`,
        };
      },
      providesTags: ["TrainingPlans"],
    }),
    createTrainingPlan: builder.mutation({
      query: (model: any) => {
        return {
          url: `pt/training_cards`,
          method: "POST",
          body: model,
        };
      },
      onQueryStarted: invalidateTrainingPlans(["TrainingPlans"]),
    }),
    deleteTrainingPlan: builder.mutation({
      query: (ids: string[]) => {
        const idsString = ids?.toString() || "";
        return {
          url: `pt/training_cards/${idsString}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateTrainingPlans(["TrainingPlans"]),
    }),
    updateTrainingPlan: builder.mutation({
      query: (model: any) => {
        return {
          url: `pt/training_cards/${model.id}`,
          method: "PUT",
          body: model,
        };
      },
      onQueryStarted: invalidateTrainingPlans(["TrainingPlans"]),
    }),
    cloneTrainingPlan: builder.mutation<
      TrainingPlanModel,
      Partial<TrainingPlanModel>
    >({
      query: (model: TrainingPlanModel) => {
        return {
          url: `pt/training_cards/${model.id}/clone`,
          method: "POST",
          body: model,
        };
      },
      onQueryStarted: invalidateTrainingPlans(["TrainingPlans"]),
    }),
    getTrainingPlan: builder.query({
      query: (id: string) => {
        return {
          url: `pt/training_cards/${id}`,
        };
      },
      providesTags: ["TrainingPlans"],
    }),
    assignTrainingPlan: builder.mutation({
      query: ({
        id,
        assignModel,
      }: {
        id: string;
        assignModel: {
          groups?: string;
          customers?: string;
          weeks?: number;
          start?: string;
        };
      }) => {
        return {
          url: `/pt/training_cards/${id}/assignments`,
          method: "POST",
          body: assignModel,
        };
      },
    }),
    getGroups: builder.query({
      query: ({ perPage, page }: { perPage: number; page: string }) => ({
        url: `pt/groups?per_page=${perPage}&page=${page}`,
      }),
    }),
    updateShowBaseExercises: builder.mutation({
      query: ({ showBaseExercises }: { showBaseExercises: number }) => {
        return {
          url: `/pt/exercises/library`,
          method: "PUT",
          body: {
            show_base_exercises: showBaseExercises,
          },
        };
      },
    }),
  }),
});

export const {
  useGetTrainingPlansQuery,
  useDeleteTrainingPlanMutation,
  useCloneTrainingPlanMutation,
  useGetTrainingPlanQuery,
  useGetGroupsQuery,
  useUpdateShowBaseExercisesMutation,
} = trainingPlansApi;
